import { useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "../../../Components/Buttons";
import { FormImage, FormInput, FormSection, FormSelect } from "../../../Components/Form";
import { ToastMessage } from "../../../Components/Utils";
import BreadCrumb from "../../../Components/BreadCrumb";
import { ROUTE_PATHS } from "../../../constants/routeConstants";
import { user_profile_image_url } from "../../../Components/ImageUrls";
import { handleError } from "../../../stores/components/handleError";
import { useFetchRolesAllQuery } from "../../../stores/api/roles.api";
import {
  useCreateUserMutation,
  useUpdateUserMutation,
} from "../../../stores/api/users.api";
import usersHelper from "../../../stores/api/helpers/users.helper";

const CreateUser = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const userdetails = location?.state?.user_details;

  const { data: rolesRaw = [] } = useFetchRolesAllQuery();
  const roles = useMemo(
    () => (rolesRaw || []).map((r) => ({ label: r.name, value: r.id })),
    [rolesRaw]
  );

  const [createUser, { isLoading: creating }] = useCreateUserMutation();
  const [updateUser, { isLoading: updating }] = useUpdateUserMutation();
  const loading = creating || updating;

  const [formData, setFormData] = useState({
    name: userdetails?.name || "",
    email: userdetails?.email || "",
    password: "",
    role_id: userdetails?.role_id || "",
    image: null,
  });

  const setField = (name, value) => setFormData((prev) => ({ ...prev, [name]: value }));

  const resetForm = () =>
    setFormData({ name: "", email: "", password: "", role_id: "", image: null });

  const onSubmit = async (e) => {
    e.preventDefault();
    try {
      if (userdetails) {
        await updateUser(usersHelper.forUpdate(userdetails.id, formData)).unwrap();
      } else {
        await createUser(usersHelper.forCreate(formData)).unwrap();
      }
      ToastMessage({
        type: "success",
        title: "Operation completed successfully.",
        message: userdetails ? "User updated successfully!" : "User created successfully!",
      });
      if (!userdetails) navigate(ROUTE_PATHS.USERS);
    } catch (err) {
      handleError(err, "Something went wrong");
    }
  };

  return (
    <div className="p-4">
      <BreadCrumb title={userdetails ? "Edit User" : "Create User"} />

      <form className="mx-auto mt-6 dark:bg-slate-900 rounded-lg p-6 space-y-5">
        <FormSection title="User Details">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormInput
              label="Name"
              isRequired
              name="name"
              value={formData.name}
              onChange={(val) => setField("name", val)}
              placeholder="Enter name"
            />

            <FormInput
              label="Email"
              isRequired
              name="email"
              value={formData.email}
              onChange={(val) => setField("email", val)}
              type="text"
              placeholder="Enter email"
            />

            <div>
              {!userdetails ? (
                <FormInput
                  label="Password"
                  isRequired
                  name="password"
                  value={formData.password}
                  onChange={(val) => setField("password", val)}
                  type="password"
                  placeholder="Enter password"
                />
              ) : (
                <div>
                  <FormInput label="Password" isRequired name="password" value="********" type="text" disabled onChange={() => {}} />
                  <small className="text-gray-500">Password cannot be updated while editing!</small>
                </div>
              )}
            </div>

            <FormSelect
              label="Role"
              isRequired
              options={roles}
              value={formData.role_id}
              onChange={(val) => setField("role_id", val ?? "")}
              blurInputOnSelect
            />
          </div>
        </FormSection>

        <FormSection title="Profile Picture">
          <FormImage
            initialPreviewUrl={userdetails?.image ? user_profile_image_url + userdetails.image : null}
            onChange={(file) => setField("image", file)}
          />
        </FormSection>

        <div className="flex items-center justify-end gap-3 pt-2">
          <Button
            type="button"
            onClick={() => {
              resetForm();
              navigate(ROUTE_PATHS.USERS);
            }}
            className="!bg-gray-100 !text-gray-700 hover:!bg-gray-200 dark:!bg-slate-800 dark:!text-gray-200"
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            className="!bg-orange-500 !text-white hover:!bg-orange-600"
            loading={loading}
            disabled={loading}
            onClick={onSubmit}
            loadingText={userdetails ? "Updating..." : "Creating..."}
          >
            {userdetails ? "Update User" : "Create User"}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default CreateUser;
